import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
  limit,
} from "firebase/firestore";
import { signOut } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle,
  ChevronRight,
  ChevronDown,
  ClipboardList,
  Eye,
  EyeOff,
  Headset,
  Home,
  LogOut,
  Moon,
  Package,
  Smartphone,
  User,
  Bell,
  MessageSquare,
  ExternalLink,
  Clock3,
  Code2,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import ApiDocument from "../components/ApiDocument";
import mtnLogo from "../assets/networks/mtn.png";
import airteltigoLogo from "../assets/networks/airteltigo.png";
import telecelLogo from "../assets/networks/telecel.png";
import {
  DEFAULT_PROMO_DISCOUNT_PERCENT,
  getPromoPrice,
  getPromoSavings,
  getPromoTiming,
} from "../utils/promoPricing";

const NETWORKS = [
  { name: "MTN", logo: mtnLogo, shortLabel: "MTN" },
  { name: "Telecel", logo: telecelLogo, shortLabel: "Telecel" },
  { name: "AirtelTigo", logo: airteltigoLogo, shortLabel: "AT" },
];

const ADMIN_PHONE =
  import.meta.env?.VITE_ADMIN_PHONE ||
  process.env.REACT_APP_ADMIN_PHONE ||
  "233552483624";

const ADMIN_SMS_PHONE = "0552483624";
const DEFAULT_MOMO_NAME = "KYETECH ADMIN";

const PAYSTACK_PUBLIC_KEY =
  import.meta.env?.VITE_PAYSTACK_KEY ||
  process.env.REACT_APP_PAYSTACK_KEY ||
  "pk_live_ed1243b2a5430c54b48c055d631c22055e1ebb43";

const MNOTIFY_KEY =
  import.meta.env?.VITE_MNOTIFY_KEY ||
  process.env.REACT_APP_MNOTIFY_KEY ||
  "YupEZ0Eo3Uguxwv1FHQZdxJCB";

const SMS_SENDER_ID =
  import.meta.env?.VITE_SMS_SENDER_ID ||
  process.env.REACT_APP_SMS_SENDER_ID ||
  "Datafie";

const getPackageCollection = (network) => {
  switch (network) {
    case "MTN":
      return "packages_MTN";
    case "AirtelTigo":
      return "packages_AirtelTigo";
    case "Telecel":
      return "packages_Telecel";
    default:
      return "packages_MTN";
  }
};

const isValidPhone = (value) => /^0\d{9}$/.test(value || "");
const isValidWebsite = (value) => {
  if (!value) return false;
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
};
const maskKey = (key) => {
  if (!key) return "";
  if (key.length <= 8) return "•".repeat(key.length);
  return `${key.slice(0, 4)}${"•".repeat(key.length - 8)}${key.slice(-4)}`;
};
const formatGhs = (value) => `GHS ${Number(value || 0).toFixed(2)}`;
const getTimeValue = (stamp) => {
  if (!stamp) return 0;
  if (typeof stamp.toDate === "function") return stamp.toDate().getTime();
  if (typeof stamp.seconds === "number") return stamp.seconds * 1000;
  return 0;
};

const generateBulkOrderId = (agentId, network, packageId, targetNumber) => {
  const cleanedNumber = String(targetNumber || "").replace(/[^0-9]/g, "");
  const cleanedPackage = String(packageId || "").replace(/[^a-zA-Z0-9_-]/g, "");
  const cleanedNetwork = String(network || "").replace(/[^a-zA-Z0-9_-]/g, "");
  return `bulk-${agentId}-${cleanedNetwork}-${cleanedPackage}-${cleanedNumber}`;
};

/* ================= CACHE SETTINGS ================= */
const CACHE_KEYS = {
  PACKAGES_PREFIX: "agent_packages_",
  HISTORY: "agent_history_cache",
  SETTINGS: "agent_settings_cache",
  CACHE_TIME: "agent_cache_time",
};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const QUERY_LIMIT = 50; // Limit packages and history

function StatusBadge({ status }) {
  const map = {
    pending: "bg-amber-500/15 text-amber-400",
    paid_pending_confirmation: "bg-amber-500/15 text-amber-400",
    processing: "bg-sky-500/15 text-sky-400",
    completed: "bg-green-500/15 text-green-400",
    approved: "bg-green-500/15 text-green-400",
    rejected: "bg-red-500/15 text-red-400",
  };

  const labels = {
    paid_pending_confirmation: "Pending",
  };

  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${map[status] || "bg-white/10 text-white"}`}>
      {labels[status] || status || "pending"}
    </span>
  );
}

function buildTopupHistory(walletRows = [], manualRows = []) {
  const paystackTopups = walletRows
    .filter((item) => item.type === "topup")
    .map((item) => ({
      id: `wallet-${item.id}`,
      amount: item.amount,
      status: item.status || "completed",
      methodLabel: item.method === "paystack" ? "Paystack Payment" : "Wallet Credit",
      title: "Wallet Top-Up",
      createdAt: item.createdAt,
      source: "wallet",
    }));

  const manualTopups = manualRows.map((item) => ({
    id: `manual-${item.id}`,
    amount: item.amount,
    status: item.status || "pending",
    methodLabel: "Direct MoMo Deposit",
    title: "Manual Top-Up",
    createdAt: item.paidAt || item.createdAt,
    paymentReference: item.paymentReference,
    source: "manual",
  }));

  return [...manualTopups, ...paystackTopups].sort(
    (a, b) => getTimeValue(b.createdAt) - getTimeValue(a.createdAt)
  );
}

function BottomNav({ activeTab, setActiveTab }) {
  const items = [
    { id: "home", label: "Home", icon: <Home size={18} /> },
    { id: "purchases", label: "Purchases", icon: <ClipboardList size={18} /> },
    { id: "support", label: "Support", icon: <Headset size={18} /> },
    { id: "profile", label: "Profile", icon: <User size={18} /> },
  ];

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-black/10 bg-white/95 px-3 py-2 backdrop-blur-xl shadow-[0_-6px_20px_rgba(15,23,42,0.08)]">
      <div className="mx-auto flex w-full max-w-md items-center justify-around rounded-2xl">
        {items.map((item) => {
          const active = activeTab === item.id;
          return (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`flex flex-col items-center gap-1 rounded-xl px-3 py-2 text-xs font-medium transition-all ${
                active ? "bg-slate-900 text-white shadow-md" : "text-slate-500 hover:text-slate-900"
              }`}
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function AgentDashboard() {
  const navigate = useNavigate();
  const [user, authLoading] = useAuthState(auth);

  const [theme, setTheme] = useState(localStorage.getItem("agent-theme") || "light");
  const [balanceHidden, setBalanceHidden] = useState(false);
  const [profile, setProfile] = useState(null);
  const [agentBalance, setAgentBalance] = useState(0);
  const [activeTab, setActiveTab] = useState("home");
  const [selectedNetwork, setSelectedNetwork] = useState("MTN");
  const [packages, setPackages] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [beneficiary, setBeneficiary] = useState("");
  const [history, setHistory] = useState([]);
  const [packagesActive, setPackagesActive] = useState(true);
  const [networkStatus, setNetworkStatus] = useState({});
  const [promoActive, setPromoActive] = useState(false);
  const [promoDiscountPercent, setPromoDiscountPercent] = useState(DEFAULT_PROMO_DISCOUNT_PERCENT);
  const [promoStartAt, setPromoStartAt] = useState(null);
  const [promoEndAt, setPromoEndAt] = useState(null);
  const [promoNow, setPromoNow] = useState(new Date());
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingPurchase, setLoadingPurchase] = useState(false);
  const [loadingTopup, setLoadingTopup] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [serviceTab, setServiceTab] = useState("data"); // "data", "airtime", or "topup"
  const [topupAmount, setTopupAmount] = useState(0);
  const [topupMethod, setTopupMethod] = useState("paystack"); // "paystack" or "manual"
  const [topupManualReason, setTopupManualReason] = useState("");
  const [manualReference, setManualReference] = useState("");
  const [manualPaymentNumber, setManualPaymentNumber] = useState(ADMIN_SMS_PHONE);
  const [manualPaymentName, setManualPaymentName] = useState(DEFAULT_MOMO_NAME);
  const [purchaseTab, setPurchaseTab] = useState("data"); // "data", "airtime", or "topups" in purchases section
  const [topups, setTopups] = useState([]);
  const [showPackagePicker, setShowPackagePicker] = useState(false);
  const [failedPurchaseDraft, setFailedPurchaseDraft] = useState(null);
  const [profileFormName, setProfileFormName] = useState("");
  const [profileFormPhone, setProfileFormPhone] = useState("");
  const [profileFormWebsite, setProfileFormWebsite] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [bulkPurchasePreview, setBulkPurchasePreview] = useState([]);
  const [processingBulk, setProcessingBulk] = useState(false);

  // Bulk Purchase: CSV Template
  const csvTemplateHeaders = [
    'Number',
    'PackageId', // e.g. 1GB, 2GB, etc.
  ];

  function handleDownloadCsvTemplate() {
    const csvContent = csvTemplateHeaders.join(',') + '\n';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_purchase_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function handleBulkCsvUpload(event) {
    const file = event.target.files[0];
    if (!file) return;

    setError("");
    setNotice("");
    setBulkPurchasePreview([]);

    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target.result;
      const rows = text.split(/\r?\n/).filter(Boolean);
      if (rows.length < 2) {
        setError('CSV file is empty or missing data.');
        return;
      }

      const [header, ...dataRows] = rows;
      const headers = header.split(',').map((h) => h.trim().toLowerCase());
      const expected = csvTemplateHeaders.map((h) => h.toLowerCase());
      if (headers.join(',') !== expected.join(',')) {
        setError('CSV headers do not match template.');
        return;
      }

      const purchases = dataRows.map((row, index) => {
        const cols = row.split(',').map((value) => value.trim());
        let number = cols[0] || "";
        if (number && !number.startsWith('0')) {
          number = '0' + number;
        }
        const packageId = cols[1] || "";
        const fullPackageId = `${selectedNetwork}_${packageId}`;
        const matchedPackage = packages.find(
          (pkg) => pkg.id === fullPackageId
        );
        const amount = matchedPackage ? Number(matchedPackage.agentPrice || matchedPackage.price || 0) : 0;
        const charge = amount; // Use admin-set price directly for bulk purchases

        return {
          row: index + 2,
          number,
          packageId,
          amount,
          charge,
          packageName: matchedPackage?.name || packageId,
          packageFound: Boolean(matchedPackage),
        };
      });

      setBulkPurchasePreview(purchases);
      setNotice(`Bulk preview ready for ${purchases.length} row${purchases.length > 1 ? 's' : ''}.`);
    };
    reader.readAsText(file);
  }

  async function handleBulkPurchase() {
    const totalCharge = bulkPurchasePreview.reduce((sum, row) => sum + row.charge, 0);
    if (agentBalance < totalCharge) {
      setError("Insufficient balance for bulk purchase.");
      return;
    }

    setProcessingBulk(true);
    setError("");
    setNotice("");

    try {
      const txnRefs = bulkPurchasePreview.map((row) =>
        doc(
          db,
          "credentials",
          generateBulkOrderId(user.uid, selectedNetwork, row.packageId, row.number)
        )
      );

      await runTransaction(db, async (transaction) => {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await transaction.get(userRef);
        const currentBalance = Number(userSnap.data().walletBalance || 0);
        if (currentBalance < totalCharge) throw new Error("Insufficient balance");

        const existingDocs = await Promise.all(txnRefs.map((ref) => transaction.get(ref)));
        existingDocs.forEach((snapshot, index) => {
          if (snapshot.exists()) {
            throw new Error(
              `Duplicate bulk order detected for row ${bulkPurchasePreview[index].row} (${bulkPurchasePreview[index].number}). Please remove duplicates and try again.`
            );
          }
        });

        transaction.update(userRef, { walletBalance: currentBalance - totalCharge });

        bulkPurchasePreview.forEach((row, index) => {
          if (row.packageFound) {
            transaction.set(txnRefs[index], {
              assignedTo: row.number,
              network: selectedNetwork,
              status: "pending",
              type: "data",
              packageId: `${selectedNetwork}_${row.packageId}`,
              totalPaid: row.charge,
              assignedAt: serverTimestamp(),
              source: "agent",
              agentId: user.uid,
              agentEmail: profile?.email || user.email,
              agentName: profile?.fullName || profile?.businessName || user.email,
            });
          }
        });
      });

      // Mirror DB writes removed — no-op to avoid duplicate IDs in mirror.

      setNotice("Bulk purchase completed. Entries saved to database and pending processing.");
      setBulkPurchasePreview([]);
      await refreshProfile();
    } catch (err) {
      setError(err.message || "Bulk purchase failed.");
    } finally {
      setProcessingBulk(false);
    }
  }

  const isDark = theme === "dark";

  useEffect(() => {
    localStorage.setItem("agent-theme", theme);
  }, [theme]);

  useEffect(() => {
    if (!promoActive) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setPromoNow(new Date());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [promoActive]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/agent/login");
    }
  }, [authLoading, navigate, user]);

  useEffect(() => {
    async function loadAgentWorkspace() {
      if (!user) {
        return;
      }

      setLoadingPage(true);
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists() || userSnap.data().role !== "agent") {
        await signOut(auth);
        navigate("/agent/login");
        return;
      }

      const userData = userSnap.data();
      setProfile({ id: user.uid, email: user.email, ...userData });
      setAgentBalance(userData.walletBalance || 0);

      // Check cache for settings
      const cachedTime = localStorage.getItem(CACHE_KEYS.CACHE_TIME);
      const cacheValid = cachedTime && Date.now() - parseInt(cachedTime) < CACHE_DURATION;
      
      if (cacheValid) {
        const cachedSettings = localStorage.getItem(CACHE_KEYS.SETTINGS);
        if (cachedSettings) {
          const settingsData = JSON.parse(cachedSettings);
          setPackagesActive(settingsData.packagesActive ?? true);
          setPromoActive(settingsData.promoActive ?? false);
          setPromoDiscountPercent(settingsData.promoDiscountPercent ?? DEFAULT_PROMO_DISCOUNT_PERCENT);
          setPromoStartAt(settingsData.promoStartAt ?? null);
          setPromoEndAt(settingsData.promoEndAt ?? null);

          const cachedPayment = localStorage.getItem("agent_payment_settings_cache");
          if (cachedPayment) {
            try {
              const paymentData = JSON.parse(cachedPayment);
              setManualPaymentNumber(paymentData.paymentNumber || ADMIN_SMS_PHONE);
              setManualPaymentName(paymentData.paymentName || DEFAULT_MOMO_NAME);
            } catch {
              localStorage.removeItem("agent_payment_settings_cache");
            }
          }

          setLoadingPage(false);
          return;
        }
      }

      // Fetch fresh settings
      const settingsSnap = await getDoc(doc(db, "settings", "packages"));
      if (settingsSnap.exists()) {
        const settingsData = settingsSnap.data();
        setPackagesActive(settingsData.packagesActive ?? true);
        setPromoActive(settingsData.promoActive ?? false);
        setPromoDiscountPercent(settingsData.promoDiscountPercent ?? DEFAULT_PROMO_DISCOUNT_PERCENT);
        setPromoStartAt(settingsData.promoStartAt ?? null);
        setPromoEndAt(settingsData.promoEndAt ?? null);
        
        // Cache the settings
        localStorage.setItem(CACHE_KEYS.SETTINGS, JSON.stringify(settingsData));
        localStorage.setItem(CACHE_KEYS.CACHE_TIME, Date.now().toString());
      }

      const networkSnap = await getDoc(doc(db, "network_status", "current"));
      if (networkSnap.exists()) {
        setNetworkStatus(networkSnap.data());
      }

      const paymentSnap = await getDoc(doc(db, "settings", "payment"));
      if (paymentSnap.exists()) {
        const paymentData = paymentSnap.data();
        setManualPaymentNumber(paymentData.paymentNumber || ADMIN_SMS_PHONE);
        setManualPaymentName(paymentData.paymentName || DEFAULT_MOMO_NAME);
        localStorage.setItem("agent_payment_settings_cache", JSON.stringify(paymentData));
      }

      setLoadingPage(false);
    }

    loadAgentWorkspace();
  }, [navigate, user]);

  useEffect(() => {
    if (!user) {
      return undefined;
    }

    const unsubscribe = onSnapshot(doc(db, "settings", "payment"), (paymentSnap) => {
      if (!paymentSnap.exists()) {
        setManualPaymentNumber(ADMIN_SMS_PHONE);
        setManualPaymentName(DEFAULT_MOMO_NAME);
        localStorage.removeItem("agent_payment_settings_cache");
        return;
      }

      const paymentData = paymentSnap.data();
      const nextPaymentSettings = {
        paymentNumber: paymentData.paymentNumber || ADMIN_SMS_PHONE,
        paymentName: paymentData.paymentName || DEFAULT_MOMO_NAME,
      };

      setManualPaymentNumber(nextPaymentSettings.paymentNumber);
      setManualPaymentName(nextPaymentSettings.paymentName);
      localStorage.setItem("agent_payment_settings_cache", JSON.stringify(nextPaymentSettings));
    });

    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    if (!profile) {
      return;
    }

    setProfileFormName(profile.fullName || profile.businessName || "");
    setProfileFormPhone(profile.phone || "");
    setProfileFormWebsite(profile.websiteUrl || "");
  }, [profile]);

  useEffect(() => {
    const colName = getPackageCollection(selectedNetwork);
    const cacheKey = CACHE_KEYS.PACKAGES_PREFIX + selectedNetwork;

    // Always hydrate from local cache first for instant UI.
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) {
          setPackages(parsed);
        }
      } catch {
        localStorage.removeItem(cacheKey);
      }
    }

    const unsubscribe = onSnapshot(collection(db, colName), (snap) => {
      const pkgs = snap.docs.map((item) => ({ id: item.id, ...item.data() }));
      setPackages(pkgs);
      localStorage.setItem(cacheKey, JSON.stringify(pkgs));
      localStorage.setItem(CACHE_KEYS.CACHE_TIME, Date.now().toString());
    });

    return () => unsubscribe();
  }, [selectedNetwork]);

  useEffect(() => {
    if (!user) {
      return undefined;
    }

    // Hydrate quickly from cache before subscribing in realtime.
    const cached = localStorage.getItem(CACHE_KEYS.HISTORY);
    if (cached) {
      try {
        setHistory(JSON.parse(cached));
      } catch {
        localStorage.removeItem(CACHE_KEYS.HISTORY);
      }
    }

    const historyQuery = query(
      collection(db, "credentials"),
      where("agentId", "==", user.uid),
      limit(QUERY_LIMIT)
    );

    const unsubscribe = onSnapshot(historyQuery, (snap) => {
      void (async () => {
        const orders = snap.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => {
            const aTime = a.assignedAt?.seconds || 0;
            const bTime = b.assignedAt?.seconds || 0;
            return bTime - aTime;
          });

        // Mirror syncing removed; use orders as-is
        setHistory(orders);
        localStorage.setItem(CACHE_KEYS.HISTORY, JSON.stringify(orders));
        localStorage.setItem(CACHE_KEYS.CACHE_TIME, Date.now().toString());
      })();
    });

    return unsubscribe;
  }, [user]);

  useEffect(() => {
    if (!user) {
      return undefined;
    }

    const cachedTime = localStorage.getItem(CACHE_KEYS.CACHE_TIME);
    if (cachedTime && Date.now() - parseInt(cachedTime) < CACHE_DURATION) {
      const cached = localStorage.getItem("agent_topups_cache");
      if (cached) {
        setTopups(JSON.parse(cached));
      }
    }

    let latestWalletTopups = [];
    let latestManualRequests = [];

    const syncTopupHistory = () => {
      const topupList = buildTopupHistory(latestWalletTopups, latestManualRequests);
      setTopups(topupList);
      localStorage.setItem("agent_topups_cache", JSON.stringify(topupList));
      localStorage.setItem(CACHE_KEYS.CACHE_TIME, Date.now().toString());
    };

    const unsubscribeWallet = onSnapshot(
      query(
        collection(db, "walletTransactions"),
        where("agentId", "==", user.uid),
        where("type", "==", "topup"),
        limit(QUERY_LIMIT)
      ),
      (snapshot) => {
        latestWalletTopups = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
        syncTopupHistory();
      }
    );

    const unsubscribeManual = onSnapshot(
      query(collection(db, "topupRequests"), where("agentId", "==", user.uid), limit(QUERY_LIMIT)),
      (snapshot) => {
        latestManualRequests = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
        syncTopupHistory();
      }
    );

    return () => {
      unsubscribeWallet();
      unsubscribeManual();
    };
  }, [user]);

  const promoTiming = useMemo(() => {
    return getPromoTiming(
      {
        promoActive,
        promoStartAt,
        promoEndAt,
      },
      promoNow
    );
  }, [promoActive, promoEndAt, promoNow, promoStartAt]);

  const effectivePromoActive = promoTiming.isDiscountActive;

  const filteredPackages = useMemo(() => {
    return packages
      .filter((pkg) => !pkg.network || pkg.network === selectedNetwork)
      .map((pkg) => {
        const basePrice = Number(pkg.agentPrice ?? pkg.price ?? 0);
        return {
          ...pkg,
          promoPrice: getPromoPrice(basePrice, effectivePromoActive, promoDiscountPercent),
          promoSavings: getPromoSavings(basePrice, effectivePromoActive, promoDiscountPercent),
        };
      })
      .sort((left, right) => left.promoPrice - right.promoPrice);
  }, [effectivePromoActive, packages, promoDiscountPercent, selectedNetwork]);

  const selectedPackage = filteredPackages.find((pkg) => pkg.id === selectedPackageId) || null;
  const selectedNetworkStatus = networkStatus?.[selectedNetwork]?.status || "good";

  async function refreshProfile() {
    if (!user) {
      return;
    }

    const snap = await getDoc(doc(db, "users", user.uid));
    if (snap.exists()) {
      const data = snap.data();
      setProfile({ id: user.uid, email: user.email, ...data });
      setAgentBalance(data.walletBalance || 0);
    }
  }

  async function refreshHistory() {
    if (!user) {
      return;
    }

    // Clear cache for history
    localStorage.removeItem(CACHE_KEYS.HISTORY);
    
    const snap = await getDocs(
      query(
        collection(db, "credentials"),
        where("agentId", "==", user.uid),
        limit(QUERY_LIMIT)
      )
    );

    const orders = snap.docs.map((item) => ({ id: item.id, ...item.data() }))
      .sort((a, b) => {
        const aTime = a.assignedAt?.seconds || 0;
        const bTime = b.assignedAt?.seconds || 0;
        return bTime - aTime;
      });
    // Mirror syncing removed; use orders directly
    setHistory(orders);
    
    // Re-cache
    localStorage.setItem(CACHE_KEYS.HISTORY, JSON.stringify(orders));
  }

  async function refreshTopups() {
    if (!user) {
      return;
    }

    // Clear cache for topups
    localStorage.removeItem("agent_topups_cache");

    const [walletSnap, manualSnap] = await Promise.all([
      getDocs(
        query(
          collection(db, "walletTransactions"),
          where("agentId", "==", user.uid),
          where("type", "==", "topup"),
          limit(QUERY_LIMIT)
        )
      ),
      getDocs(query(collection(db, "topupRequests"), where("agentId", "==", user.uid), limit(QUERY_LIMIT))),
    ]);

    const topupList = buildTopupHistory(
      walletSnap.docs.map((item) => ({ id: item.id, ...item.data() })),
      manualSnap.docs.map((item) => ({ id: item.id, ...item.data() }))
    );
    setTopups(topupList);
    
    // Re-cache
    localStorage.setItem("agent_topups_cache", JSON.stringify(topupList));
  }

  async function handlePurchase() {
    setError("");
    setNotice("");
    setFailedPurchaseDraft(null);

    if (!packagesActive) {
      setError("Package purchasing is currently offline.");
      return;
    }

    if (!selectedPackage) {
      setError("Select a package first.");
      return;
    }

    if (selectedPackage.outOfStock) {
      setError("Selected package is out of stock.");
      return;
    }

    if (!isValidPhone(beneficiary)) {
      setError("Enter a valid beneficiary phone number.");
      return;
    }

    if (["bad", "down"].includes(String(selectedNetworkStatus || "").toLowerCase())) {
      setError(`${selectedNetwork} network is currently bad.`);
      return;
    }

    const cleanBeneficiary = beneficiary.trim();
    const orderAmount = Number(selectedPackage.promoPrice);

    if (!Number.isFinite(orderAmount) || orderAmount <= 0) {
      setError("Selected package has an invalid price. Contact admin to update package pricing.");
      return;
    }

    setLoadingPurchase(true);

    try {
      await runTransaction(db, async (transaction) => {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await transaction.get(userRef);

        if (!userSnap.exists()) {
          throw new Error("Agent profile not found.");
        }

        const walletBalance = Number(userSnap.data().walletBalance || 0);
        if (!Number.isFinite(walletBalance)) {
          throw new Error("Wallet balance is invalid. Please contact admin.");
        }

        if (walletBalance < orderAmount) {
          throw new Error("Insufficient wallet balance.");
        }

        const nextBalance = Number((walletBalance - orderAmount).toFixed(2));
        const orderRef = doc(collection(db, "credentials"));
        const walletRef = doc(collection(db, "walletTransactions"));

        transaction.update(userRef, {
          walletBalance: nextBalance,
          updatedAt: serverTimestamp(),
        });

        transaction.set(orderRef, {
          assignedTo: cleanBeneficiary,
          network: selectedNetwork,
          status: "pending",
          type: "data",
          packageId: selectedPackage.id,
          totalPaid: orderAmount,
          assignedAt: serverTimestamp(),
          source: "agent",
          agentId: user.uid,
          agentEmail: profile?.email || user.email,
          agentName: profile?.fullName || profile?.businessName || user.email,
        });

        transaction.set(walletRef, {
          agentId: user.uid,
          agentEmail: profile?.email || user.email,
          agentName: profile?.fullName || profile?.businessName || user.email,
          amount: -orderAmount,
          balanceAfter: nextBalance,
          type: "purchase",
          method: "wallet",
          status: "completed",
          packageId: selectedPackage.id,
          beneficiary: cleanBeneficiary,
          network: selectedNetwork,
          note: "Agent wallet data purchase",
          createdAt: serverTimestamp(),
        });
      });

      // Mirror DB single-purchase write removed to prevent duplicate entries.

      setNotice(`Purchase placed successfully for ${cleanBeneficiary}.`);
      setBeneficiary("");
      setSelectedPackageId("");
      await refreshProfile();
      await refreshHistory();
      setActiveTab("purchases");
    } catch (purchaseError) {
      const code = String(purchaseError?.code || "").toLowerCase();
      const message = String(purchaseError?.message || "");
      const purchaseDraft = {
        network: selectedNetwork,
        packageId: selectedPackage?.id || "",
        beneficiary: cleanBeneficiary,
        amount: orderAmount,
      };

      if (code.includes("permission-denied")) {
        setError("Purchase blocked by Firestore rules. Ask admin to grant agent write access.");
      } else if (code.includes("resource-exhausted") || /quota\s*exceeded/i.test(message)) {
        setFailedPurchaseDraft(purchaseDraft);
        setError("Purchase failed because Firestore quota is exceeded. Use Send to Admin below to complete this order manually.");
      } else if (code.includes("unavailable")) {
        setError("Purchase service is temporarily unavailable. Please try again shortly.");
      } else {
        setError(message || "Unable to place purchase right now.");
      }
    } finally {
      setLoadingPurchase(false);
    }
  }

  async function handleSimSetup() {
    const defaultNumber = localStorage.getItem("agent-sim-number") || profile?.phone || "";
    const value = window.prompt("Enter your SIM number (10 digits, starts with 0)", defaultNumber);

    if (value === null) {
      return;
    }

    const cleanValue = value.trim();
    if (!isValidPhone(cleanValue)) {
      setError("Invalid phone number. Use 10 digits starting with 0.");
      return;
    }

    try {
      localStorage.setItem("agent-sim-number", cleanValue);
      await updateDoc(doc(db, "users", profile.id), {
        phone: cleanValue,
        updatedAt: serverTimestamp(),
      });
      await refreshProfile();
      setNotice("SIM number updated successfully.");
    } catch {
      setError("Unable to save SIM number right now.");
    }
  }

  async function handleProfileUpdate() {
    setNotice("");
    setError("");

    const cleanName = String(profileFormName || "").trim();
    const cleanPhone = String(profileFormPhone || "").trim();
    const cleanWebsite = String(profileFormWebsite || "").trim();

    if (!cleanName) {
      setError("Enter your full name.");
      return;
    }

    if (!isValidPhone(cleanPhone)) {
      setError("Enter a valid phone number (10 digits, starts with 0).");
      return;
    }

    if (cleanWebsite && !isValidWebsite(cleanWebsite)) {
      setError("Enter a valid website URL, including http:// or https://.");
      return;
    }

    setSavingProfile(true);
    try {
      await updateDoc(doc(db, "users", profile.id), {
        fullName: cleanName,
        phone: cleanPhone,
        websiteUrl: cleanWebsite,
        updatedAt: serverTimestamp(),
      });

      localStorage.setItem("agent-sim-number", cleanPhone);
      await refreshProfile();
      setNotice("Profile updated successfully.");
    } catch (profileError) {
      setError(profileError?.message || "Unable to update profile right now.");
    } finally {
      setSavingProfile(false);
    }
  }

  async function handleTopupWithPaystack() {
    setError("");
    setNotice("");

    if (topupAmount <= 0) {
      setError("Enter a valid top-up amount.");
      return;
    }

    if (!profile?.email && !user?.email) {
      setError("Email not found. Unable to proceed.");
      return;
    }

    setLoadingTopup(true);

    try {
      if (!window?.PaystackPop?.setup) {
        throw new Error("Payment service is not available right now. Please refresh and try again.");
      }

      const onPaystackSuccess = (res) => {
        const reference =
          res?.reference ||
          res?.trxref ||
          res?.trans ||
          "";

        if (!reference) {
          setLoadingTopup(false);
          setError("Payment callback received without reference. Please contact admin.");
          return;
        }

        void handleTopupSuccess(reference);
      };

      const handler = window.PaystackPop.setup({
        key: PAYSTACK_PUBLIC_KEY,
        email: profile?.email || user.email,
        amount: Math.round(Number(topupAmount) * 100),
        currency: "GHS",
        ref: `TOPUP-${user.uid}-${Date.now()}`,
        onClose: () => {
          setLoadingTopup(false);
          setError("Top-up payment was cancelled.");
        },
        callback: onPaystackSuccess,
      });

      if (!handler?.openIframe) {
        throw new Error("Unable to open payment window. Please try again.");
      }

      handler.openIframe();
    } catch (paystackError) {
      const rawMessage = String(paystackError?.message || "");
      const isQuotaExceeded = /quota\s*exceeded/i.test(rawMessage);

      setLoadingTopup(false);

      if (isQuotaExceeded) {
        setTopupMethod("manual");
        setError("Paystack quota exceeded. Please use Request (Admin) for now or update your Paystack public key.");
        return;
      }

      setError(rawMessage || "Unable to start payment. Please try again.");
    }
  }

  async function handleTopupSuccess(ref) {
    try {
      const userRef = doc(db, "users", user.uid);
      const currentBalance = Number(profile?.walletBalance || 0);
      const newBalance = currentBalance + topupAmount;

      await updateDoc(userRef, {
        walletBalance: newBalance,
        updatedAt: serverTimestamp(),
      });

      await addDoc(collection(db, "walletTransactions"), {
        agentId: user.uid,
        amount: topupAmount,
        balanceAfter: newBalance,
        type: "topup",
        method: "paystack",
        paystackRef: ref,
        createdAt: serverTimestamp(),
      });

      await refreshProfile();
      await refreshTopups();
      setNotice(`Top-up successful! GHS ${topupAmount.toFixed(2)} added to wallet.`);
      setTopupAmount(0);
      setLoadingTopup(false);
    } catch (err) {
      const errCode = String(err?.code || "").toLowerCase();
      const errMessage = String(err?.message || "");
      const quotaExceeded =
        errCode.includes("resource-exhausted") ||
        /quota\s*exceeded/i.test(errMessage);

      if (quotaExceeded) {
        setError("Payment may have completed, but Firestore quota exceeded while saving. Contact admin with your Paystack reference.");
      } else {
        setError("Top-up failed. Please contact admin.");
      }
      setLoadingTopup(false);
    }
  }

  function generateManualReference() {
    const letter = String.fromCharCode(65 + Math.floor(Math.random() * 26)); // A-Z
    const numbers = Math.floor(Math.random() * 1000).toString().padStart(3, '0'); // 000-999
    const ref = `${letter}${numbers}`;
    setManualReference(ref);
    return ref;
  }

  function normalizePhone(phoneNumber) {
    const raw = String(phoneNumber || "").replace(/\s+/g, "");
    if (raw.startsWith("0") && raw.length === 10) {
      return `233${raw.slice(1)}`;
    }
    return raw;
  }

  async function sendAdminSms(message) {
    const recipient = normalizePhone(ADMIN_SMS_PHONE);
    const response = await fetch(`https://api.mnotify.com/api/sms/quick?key=${MNOTIFY_KEY}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        recipient: [recipient],
        sender: SMS_SENDER_ID,
        message,
      }),
    });

    if (!response.ok) {
      throw new Error("Unable to send SMS alert to admin.");
    }
  }

  async function handleManualPaymentConfirmation() {
    setError("");
    setNotice("");

    if (topupAmount <= 0) {
      setError("Enter a valid top-up amount before confirming payment.");
      return;
    }

    if (!String(manualReference || "").trim()) {
      setError("Generate a payment reference before tapping I Have Paid.");
      return;
    }

    setLoadingTopup(true);

    try {
      const paymentReference = manualReference.trim();
      const agentName = profile?.fullName || profile?.businessName || user?.email || "Agent";

      await addDoc(collection(db, "topupRequests"), {
        agentId: user.uid,
        agentEmail: profile?.email || user.email,
        agentName,
        amount: topupAmount,
        reason: topupManualReason.trim(),
        status: "paid_pending_confirmation",
        paymentChannel: "momo_direct_deposit",
        paymentReference,
        momoNumber: manualPaymentNumber,
        momoName: manualPaymentName,
        paidAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      });

      const smsMessage = [
        "MANUAL TOPUP ALERT",
        `AGENT NAME: ${agentName}`,
        `AMOUNT: GHS ${Number(topupAmount).toFixed(2)}`,
        `REFERENCE: ${paymentReference}`,
      ].join("\n");

      await sendAdminSms(smsMessage);

      setNotice("Payment confirmation sent to admin by SMS. Wallet will be credited after verification.");
      setTopupAmount(0);
      setTopupManualReason("");
      setManualReference("");
      setLoadingTopup(false);
    } catch (confirmError) {
      setError(confirmError.message || "Unable to confirm manual payment right now.");
      setLoadingTopup(false);
    }
  }

  async function handleLogout() {
    await signOut(auth);
    navigate("/agent/login");
  }

  function openAdminPurchaseRequest() {
    if (!failedPurchaseDraft) {
      return;
    }

    const walletBalance = Number(profile?.walletBalance || 0).toFixed(2);
    const message = [
      "Hello Admin, manual data purchase request.",
      `Agent: ${profile?.fullName || profile?.businessName || user?.email || "Unknown"}`,
      `Agent Email: ${profile?.email || user?.email || "N/A"}`,
      `Network: ${failedPurchaseDraft.network}`,
      `Package: ${failedPurchaseDraft.packageId}`,
      `Beneficiary: ${failedPurchaseDraft.beneficiary}`,
      `Amount: GHS ${Number(failedPurchaseDraft.amount || 0).toFixed(2)}`,
      `Wallet Shown: GHS ${walletBalance}`,
      `Reason: Firestore quota exceeded on agent dashboard.`,
    ].join("\n");

    const waUrl = `https://wa.me/${ADMIN_PHONE}?text=${encodeURIComponent(message)}`;
    window.open(waUrl, "_blank", "noopener,noreferrer");
  }

  const profileName = profile?.fullName || profile?.businessName || user?.email || "Agent";
  const firstName = profileName.split(" ")[0] || "A";
  const profileEmail = profile?.email || user?.email || "No email";
  const profilePhone = profile?.phone || localStorage.getItem("agent-sim-number") || "Not set";
  const savedSimNumber = localStorage.getItem("agent-sim-number") || profile?.phone || "Not set";
  const totalPurchases = history.filter((item) => item.type === "data" || item.type === "airtime" || !item.type).length;
  const totalTopups = topups.length;
  const profileFormChanged =
    String(profileFormName || "").trim() !== String(profile?.fullName || profile?.businessName || "").trim() ||
    String(profileFormPhone || "").trim() !== String(profile?.phone || "").trim() ||
    String(profileFormWebsite || "").trim() !== String(profile?.websiteUrl || "").trim();

  if (authLoading || loadingPage || !profile) {
    return (
      <div className="min-h-screen bg-slate-100 text-slate-900 flex items-center justify-center">
        <div className="rounded-2xl bg-white px-6 py-5 shadow-lg">Loading agent workspace...</div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen pb-24 ${isDark ? "bg-slate-950 text-white" : "bg-slate-100 text-slate-900"}`}>
      <div className="mx-auto flex min-h-screen max-w-6xl flex-col gap-6 px-4 py-5 md:px-6">
        {(notice || error) && (
          <div className={`rounded-2xl border px-4 py-3 text-sm font-medium ${error ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
            {error || notice}
          </div>
        )}

        {activeTab === "home" && (
          <div className="mx-auto w-full max-w-md space-y-4">
            {/* App Header */}
            <div className="flex items-center justify-between">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-sm font-black uppercase text-white shadow-sm">
                {firstName.slice(0, 1)}
              </div>
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#274193]">
                  <span className="text-[9px] font-black leading-none text-white">DF</span>
                </div>
                <span className={`text-base font-extrabold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>Data fie</span>
              </div>
              <button className={`rounded-full border p-2 ${isDark ? "border-slate-700 text-slate-300" : "border-slate-200 text-slate-500"}`}>
                <Bell size={18} />
              </button>
            </div>

            {/* Greeting */}
            <div>
              <p className={`text-xl font-bold ${isDark ? "text-white" : "text-slate-900"}`}>Hi, {profile.fullName?.split(" ")[0] || "Agent"}</p>
              <p className={`text-sm ${isDark ? "text-white/60" : "text-slate-500"}`}>Buy affordable data bundles & more</p>
            </div>

            {/* Wallet Balance Card */}
            <div className={`rounded-3xl border p-5 shadow-sm ${isDark ? "border-white/10 bg-slate-900" : "border-slate-100 bg-white"}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <div className={`rounded-md p-1 ${isDark ? "bg-slate-700" : "bg-slate-100"}`}>
                      <svg className={`h-3.5 w-3.5 ${isDark ? "text-slate-300" : "text-slate-500"}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <rect x="2" y="5" width="20" height="14" rx="2"/>
                        <path d="M2 10h20"/>
                      </svg>
                    </div>
                    <p className={`text-xs font-semibold ${isDark ? "text-white/60" : "text-slate-500"}`}>Wallet Balance</p>
                  </div>
                  <p className={`mt-3 text-3xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>
                    {balanceHidden ? "GH\u00a2 \u25cf\u25cf\u25cf\u25cf\u25cf" : `GH\u00a2 ${Number(profile.walletBalance || 0).toFixed(2)}`}
                  </p>
                </div>
                <button
                  onClick={() => setBalanceHidden((h) => !h)}
                  className={`rounded-full p-1 ${isDark ? "text-slate-400" : "text-slate-400"}`}
                >
                  {balanceHidden ? <Eye size={22} /> : <EyeOff size={22} />}
                </button>
              </div>
            </div>

            {/* Service Tabs */}
            <div className={`flex rounded-full p-1 ${isDark ? "bg-slate-800" : "bg-slate-100"}`}>
              <button
                onClick={() => setServiceTab("data")}
                className={`flex-1 rounded-full py-2.5 text-sm font-bold transition-all ${serviceTab === "data" ? "bg-[#274193] text-white shadow-sm" : isDark ? "text-slate-300" : "text-slate-600"}`}>
                Buy Data
              </button>
              <button
                onClick={() => setServiceTab("airtime")}
                className={`flex-1 rounded-full py-2.5 text-sm font-bold transition-all ${serviceTab === "airtime" ? "bg-[#274193] text-white shadow-sm" : isDark ? "text-slate-300" : "text-slate-600"}`}>
                Airtime
              </button>
              <button
                onClick={() => setServiceTab("topup")}
                className={`flex-1 rounded-full py-2.5 text-sm font-bold transition-all ${serviceTab === "topup" ? "bg-[#274193] text-white shadow-sm" : isDark ? "text-slate-300" : "text-slate-600"}`}>
                Top Up
              </button>
            </div>

            {/* Buy Data Form */}
            {serviceTab === "data" && (
            <div className={`rounded-3xl border p-5 space-y-4 ${isDark ? "border-white/10 bg-slate-900" : "border-slate-200 bg-white"}`}>
              {/* Bulk Purchase Section */}
              <div className="mb-4 rounded-2xl border border-blue-200 bg-blue-50 p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div>
                    <p className="text-sm font-bold text-blue-900">Bulk Purchase</p>
                    <p className="text-xs text-blue-700">Download template, fill, and upload for multiple purchases.</p>
                  </div>
                  <div className="flex gap-2 items-center">
                    <button
                      type="button"
                      onClick={handleDownloadCsvTemplate}
                      className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-blue-700"
                    >
                      Download CSV Template
                    </button>
                    <label className="inline-block cursor-pointer rounded-lg bg-blue-100 px-3 py-1.5 text-xs font-bold text-blue-900 hover:bg-blue-200">
                      Upload CSV
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleBulkCsvUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>

                {bulkPurchasePreview.length > 0 && (
                  <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-800 shadow-sm">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold">Bulk Upload Preview</p>
                        <p className="text-xs text-slate-500">Review the numbers, package IDs, and charge based on current package pricing.</p>
                      </div>
                      <p className="text-right text-xs font-semibold text-slate-600">Total amount: {formatGhs(bulkPurchasePreview.reduce((sum, row) => sum + Number(row.amount || 0), 0))} | Total charge: {formatGhs(bulkPurchasePreview.reduce((sum, row) => sum + Number(row.charge || 0), 0))}</p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-left text-sm">
                        <thead>
                          <tr>
                            <th className="px-2 py-2 font-semibold text-slate-600">Row</th>
                            <th className="px-2 py-2 font-semibold text-slate-600">Number</th>
                            <th className="px-2 py-2 font-semibold text-slate-600">Package</th>
                            <th className="px-2 py-2 font-semibold text-slate-600">Amount</th>
                            <th className="px-2 py-2 font-semibold text-slate-600">Charge</th>
                            <th className="px-2 py-2 font-semibold text-slate-600">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bulkPurchasePreview.map((row) => (
                            <tr key={row.row} className={row.packageFound ? "border-t border-slate-200" : "border-t border-red-200 bg-red-50"}>
                              <td className="px-2 py-2">{row.row}</td>
                              <td className="px-2 py-2">{row.number || "—"}</td>
                              <td className="px-2 py-2">{row.packageName}</td>
                              <td className="px-2 py-2">{row.packageFound ? formatGhs(row.amount) : "—"}</td>
                              <td className="px-2 py-2">{row.packageFound ? formatGhs(row.charge) : "—"}</td>
                              <td className="px-2 py-2 font-semibold text-sm text-slate-700">
                                {row.packageFound ? "OK" : "Package not found"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {bulkPurchasePreview.every(row => row.packageFound) && bulkPurchasePreview.length > 0 && (
                      <div className="mt-4 flex justify-end">
                        <button
                          onClick={handleBulkPurchase}
                          disabled={processingBulk}
                          className="px-4 py-2 rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700 disabled:opacity-50"
                        >
                          {processingBulk ? "Processing..." : "Buy Bulk"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Network Selection */}
              <div>
                <label className="mb-3 block text-sm font-semibold">Network</label>
                <div className="grid grid-cols-3 gap-2">
                  {NETWORKS.map((network) => (
                    <button
                      key={network.name}
                      onClick={() => setSelectedNetwork(network.name)}
                      className={`flex items-center justify-center gap-2 rounded-full border-2 px-3 py-3 text-sm font-bold transition-all ${selectedNetwork === network.name ? "border-yellow-400 bg-yellow-100 text-slate-900" : isDark ? "border-white/10 bg-white/5 text-white" : "border-slate-200 bg-slate-50 text-slate-700"}`}
                    >
                      <img src={network.logo} alt={network.name} className="h-5 w-5 object-contain" />
                      <span>{network.shortLabel}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Package Selection */}
              <div>
                <label className="mb-2 block text-sm font-semibold">Package</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowPackagePicker(true)}
                    className={`relative w-full rounded-2xl border px-4 py-3 text-left text-base font-semibold outline-none transition-all ${isDark ? "border-slate-600 bg-slate-900 text-slate-100 focus:border-[#274193] focus:ring-2 focus:ring-[#274193]/20" : "border-slate-300 bg-white text-slate-800 focus:border-[#274193] focus:ring-2 focus:ring-[#274193]/20"}`}
                  >
                    {selectedPackage ? `${selectedPackage.id} - ${formatGhs(selectedPackage.promoPrice)}` : "Select Package"}
                  </button>
                  <ChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                </div>
                {selectedPackage && effectivePromoActive && selectedPackage.promoSavings > 0 && (
                  <p className="mt-2 text-xs font-semibold text-emerald-500">
                    Promo price active. Save {formatGhs(selectedPackage.promoSavings)} on this package.
                  </p>
                )}
              </div>

              {/* Beneficiary Phone Number */}
              <div>
                <label className="mb-2 block text-sm font-semibold">Beneficiary (Phone Number)</label>
                <input
                  value={beneficiary}
                  onChange={(event) => setBeneficiary(event.target.value)}
                  placeholder="024xxxxxxx"
                  maxLength={10}
                    className={`w-full rounded-2xl border px-4 py-3 text-base font-semibold outline-none transition-all ${isDark ? "border-slate-600 bg-white text-slate-900 focus:border-[#274193] focus:ring-2 focus:ring-[#274193]/20" : "border-slate-300 bg-white text-slate-900 focus:border-[#274193] focus:ring-2 focus:ring-[#274193]/20"}`}
                />
              </div>

              {/* Buy Data Button */}
              <button
                onClick={handlePurchase}
                disabled={loadingPurchase || !packagesActive || !beneficiary || !selectedPackageId}
                className={`w-full rounded-full px-6 py-4 text-lg font-bold transition-all ${
                  !loadingPurchase && packagesActive && beneficiary && selectedPackageId
                    ? "bg-[#274193] text-white hover:bg-[#1e3070]"
                    : "bg-[#e5e5ea] text-slate-500"
                }`}
              >
                {loadingPurchase ? "Processing..." : "Buy Data"}
              </button>

              {failedPurchaseDraft && (
                <button
                  onClick={openAdminPurchaseRequest}
                  className="w-full rounded-full border border-emerald-300 bg-emerald-50 px-6 py-4 text-sm font-bold text-emerald-700 transition-all hover:bg-emerald-100"
                >
                  Send Purchase To Admin
                </button>
              )}
            </div>
            )}

            {serviceTab === "airtime" && (
              <div className={`flex flex-col items-center justify-center rounded-3xl border py-16 text-center ${isDark ? "border-white/10 bg-slate-900" : "border-slate-200 bg-white"}`}>
                <div className={`mb-6 flex h-32 w-32 items-center justify-center rounded-full ${isDark ? "bg-slate-800" : "bg-slate-200"}`}>
                  <Package size={56} strokeWidth={1.5} className={isDark ? "text-slate-400" : "text-slate-500"} />
                </div>

                <h3 className={`text-xl font-bold ${isDark ? "text-white" : "text-slate-900"}`}>We are working round the</h3>
                <p className={`mt-1 max-w-[260px] text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  clock to bring this feature to you.
                </p>
              </div>
            )}

            {/* Top Up Form */}
            {serviceTab === "topup" && (
            <div className={`rounded-3xl border p-5 space-y-4 ${isDark ? "border-white/10 bg-slate-900" : "border-slate-200 bg-white"}`}>
              {/* Amount Input with inline +/- controls */}
              <div>
                <label className="mb-2 block text-sm font-semibold">Amount (GHS)</label>
                <div className={`relative rounded-2xl border-2 border-[#274193] ${isDark ? "bg-white/5" : "bg-slate-50"}`}>
                  <input
                    type="number"
                    min="0"
                    value={topupAmount === 0 ? "" : topupAmount}
                    onChange={(e) => setTopupAmount(Math.max(0, Number(e.target.value) || 0))}
                    className={`w-full bg-transparent py-3 pl-12 pr-12 text-center text-2xl font-black outline-none ${isDark ? "text-white" : "text-slate-900"}`}
                    placeholder="0"
                  />
                  <button
                    type="button"
                    onClick={() => setTopupAmount(Math.max(0, topupAmount - 10))}
                    className="absolute left-2 top-1/2 -translate-y-1/2 rounded-lg bg-slate-200 px-2.5 py-1.5 text-sm font-bold text-slate-700"
                  >
                    −
                  </button>
                  <button
                    type="button"
                    onClick={() => setTopupAmount(topupAmount + 10)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg bg-slate-200 px-2.5 py-1.5 text-sm font-bold text-slate-700"
                  >
                    +
                  </button>
                </div>
                {topupAmount > 0 && topupAmount < 20 && (
                  <p className="mt-1.5 text-xs font-semibold text-red-500">Invalid input</p>
                )}
              </div>

              {/* Quick Amount Buttons */}
              <div className="grid grid-cols-4 gap-2">
                {[50, 100, 200, 500].map((amount) => (
                  <button 
                    key={amount}
                    onClick={() => setTopupAmount(amount)}
                    className={`rounded-xl px-3 py-2 text-sm font-bold transition-all ${topupAmount === amount ? "bg-[#274193] text-white" : isDark ? "bg-white/5 text-white" : "bg-slate-100 text-slate-600"}`}
                  >
                    {amount}
                  </button>
                ))}
              </div>

              {/* Payment Method Selection */}
              <div>
                <label className="mb-2 block text-sm font-semibold">Payment Method</label>
                <div className="grid grid-cols-2 gap-2">
                  <button 
                    onClick={() => setTopupMethod("paystack")}
                    className={`rounded-2xl border-2 px-4 py-3 text-sm font-bold transition-all ${topupMethod === "paystack" ? "border-[#274193] bg-blue-50 text-[#274193]" : isDark ? "border-white/20 text-white" : "border-slate-300 text-slate-600"}`}
                  >
                    Paystack (Instant)
                  </button>
                  <button 
                    onClick={() => setTopupMethod("manual")}
                    className={`rounded-2xl border-2 px-4 py-3 text-sm font-bold transition-all ${topupMethod === "manual" ? "border-[#274193] bg-blue-50 text-[#274193]" : isDark ? "border-white/20 text-white" : "border-slate-300 text-slate-600"}`}
                  >
                    Request (Admin)
                  </button>
                </div>
              </div>

              {/* Manual Request Reason */}
              {topupMethod === "manual" && (
                <div>
                  <div className={`mb-3 rounded-2xl border p-4 ${isDark ? "border-amber-500/30 bg-amber-500/10" : "border-amber-200 bg-amber-50"}`}>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-amber-700">Direct Deposit Details</p>
                    <p className="mt-2 text-sm font-semibold text-slate-700">MOMO number: {manualPaymentNumber}</p>
                    <p className="text-sm font-semibold text-slate-700">Name: {manualPaymentName}</p>
                    <p className="mt-2 text-xs text-slate-600">Use this reference when sending payment:</p>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-bold text-white">
                        {manualReference || "Not generated"}
                      </span>
                      <button
                        type="button"
                        onClick={generateManualReference}
                        className="rounded-lg bg-[#274193] px-3 py-1.5 text-xs font-bold text-white"
                      >
                        Generate Ref
                      </button>
                    </div>
                  </div>

                  <label className="mb-2 block text-sm font-semibold">Reason (Optional)</label>
                  <textarea
                    value={topupManualReason}
                    onChange={(e) => setTopupManualReason(e.target.value)}
                    placeholder="Provide context for your top-up request..."
                    rows={3}
                      className={`w-full rounded-2xl border px-4 py-3 text-base font-semibold outline-none transition-all ${isDark ? "border-slate-600 bg-white text-slate-900 focus:border-[#274193] focus:ring-2 focus:ring-[#274193]/20" : "border-slate-300 bg-white text-slate-900 focus:border-[#274193] focus:ring-2 focus:ring-[#274193]/20"}`}
                  />
                </div>
              )}

              {/* Top Up Button */}
              <button
                onClick={topupMethod === "paystack" ? handleTopupWithPaystack : handleManualPaymentConfirmation}
                disabled={loadingTopup || topupAmount < 20 || (topupMethod === "manual" && !String(manualReference || "").trim())}
                className={`w-full rounded-full px-6 py-4 text-lg font-bold transition-all ${
                  !loadingTopup && topupAmount >= 20 && (topupMethod === "paystack" || String(manualReference || "").trim())
                    ? "bg-slate-900 text-white hover:bg-slate-800"
                    : "bg-[#e5e5ea] text-slate-500"
                }`}
              >
                {loadingTopup ? "Processing..." : topupMethod === "paystack" ? "Top Up Wallet" : "I Have Paid"}
              </button>

              {topupMethod === "manual" && (
                <p className={`text-center text-xs ${isDark ? "text-white/60" : "text-slate-500"}`}>
                  Generate a reference first, then tap I Have Paid to alert admin via SMS on {ADMIN_SMS_PHONE}.
                </p>
              )}
            </div>
            )}
          </div>
        )}

        {activeTab === "purchases" && (
          <div className="mx-auto w-full max-w-md space-y-4">
            {/* Header */}
            <div>
              <h2 className={`text-2xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>Purchases</h2>
            </div>

            {/* Tabs */}
            <div className="flex gap-2">
              <button
                onClick={() => setPurchaseTab("data")}
                className={`flex-1 rounded-2xl px-4 py-3 text-sm font-bold transition-all ${
                  purchaseTab === "data"
                    ? "bg-[#274193] text-white"
                    : isDark
                    ? "bg-slate-800 text-white"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                Data
              </button>
              <button
                onClick={() => setPurchaseTab("airtime")}
                className={`flex-1 rounded-2xl px-4 py-3 text-sm font-bold transition-all ${
                  purchaseTab === "airtime"
                    ? "bg-[#274193] text-white"
                    : isDark
                    ? "bg-slate-800 text-white"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                Airtime
              </button>
              <button
                onClick={() => setPurchaseTab("topups")}
                className={`flex-1 rounded-2xl px-4 py-3 text-sm font-bold transition-all ${
                  purchaseTab === "topups"
                    ? "bg-[#274193] text-white"
                    : isDark
                    ? "bg-slate-800 text-white"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                Top-Ups
              </button>
            </div>

            {/* Purchase Cards */}
            {(purchaseTab === "data" || purchaseTab === "airtime") && (
              <div className="space-y-3">
                {history.length === 0 ? (
                  <div
                    className={`rounded-2xl border p-5 text-center ${
                      isDark ? "border-white/10 bg-slate-900" : "border-slate-300 bg-slate-50"
                    }`}
                  >
                    <p className={isDark ? "text-white/60" : "text-slate-500"}>No {purchaseTab} purchases yet.</p>
                  </div>
                ) : (
                  history
                    .filter((item) => item.type === purchaseTab || !item.type)
                    .map((item) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`rounded-2xl border p-4 ${isDark ? "border-white/10 bg-slate-900" : "border-slate-300 bg-white"}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xl">😊</span>
                              <div>
                                <p className="text-sm font-bold text-slate-900">{item.packageId}</p>
                                <p className={`text-xs ${isDark ? "text-white/60" : "text-slate-500"}`}>
                                  {item.assignedTo}
                                </p>
                              </div>
                            </div>
                            <p className={`mt-1 text-xs ${isDark ? "text-white/50" : "text-slate-400"}`}>
                              {item.assignedAt?.toDate ? item.assignedAt.toDate().toLocaleDateString() : "Pending"}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-black">{formatGhs(item.totalPaid)}</p>
                            <StatusBadge status={item.status} />
                          </div>
                          <ChevronDown size={20} className={isDark ? "text-white/40" : "text-slate-400"} />
                        </div>
                      </motion.div>
                    ))
                )}
              </div>
            )}

            {/* Top-Ups Cards */}
            {purchaseTab === "topups" && (
              <div className="space-y-3">
                {topups.length === 0 ? (
                  <div
                    className={`rounded-2xl border p-5 text-center ${
                      isDark ? "border-white/10 bg-slate-900" : "border-slate-300 bg-slate-50"
                    }`}
                  >
                    <p className={isDark ? "text-white/60" : "text-slate-500"}>No top-ups yet.</p>
                  </div>
                ) : (
                  topups.map((item) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`rounded-2xl border p-4 ${isDark ? "border-white/10 bg-slate-900" : "border-slate-300 bg-white"}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">➕</span>
                            <div>
                              <p className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{item.title || "Wallet Top-Up"}</p>
                              <p className={`text-xs ${isDark ? "text-white/60" : "text-slate-500"}`}>
                                {item.methodLabel || (item.method === "paystack" ? "Paystack Payment" : "Admin Credit")}
                              </p>
                              {item.paymentReference && (
                                <p className={`mt-1 text-[11px] ${isDark ? "text-white/50" : "text-slate-400"}`}>
                                  Ref: {item.paymentReference}
                                </p>
                              )}
                            </div>
                          </div>
                          <p className={`mt-1 text-xs ${isDark ? "text-white/50" : "text-slate-400"}`}>
                            {item.createdAt?.toDate ? item.createdAt.toDate().toLocaleDateString() : "Pending"}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-black ${["approved", "completed"].includes(String(item.status || "").toLowerCase()) ? "text-emerald-500" : String(item.status || "").toLowerCase() === "rejected" ? "text-red-500" : "text-amber-500"}`}>
                            {["approved", "completed"].includes(String(item.status || "").toLowerCase()) ? "+" : ""}{formatGhs(item.amount)}
                          </p>
                          <StatusBadge status={item.status} />
                        </div>
                        <ChevronDown size={20} className={isDark ? "text-white/40" : "text-slate-400"} />
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === "support" && (
          <div className="mx-auto w-full max-w-md space-y-4">
            <div className="text-center">
              <p className={`text-3xl font-extrabold ${isDark ? "text-white" : "text-slate-900"}`}>How can we help?</p>
              <p className={`mt-1 text-sm ${isDark ? "text-slate-300" : "text-slate-500"}`}>Get in touch with our team via WhatsApp</p>
            </div>

            <div className="relative overflow-hidden rounded-3xl bg-[#20ba56] p-5 text-white shadow-[0_10px_24px_rgba(16,185,129,0.28)]">
              <div className="absolute -right-10 -top-8 h-28 w-28 rounded-full bg-white/10" />
              <div className="absolute -right-4 bottom-[-32px] h-28 w-28 rounded-full bg-white/10" />

              <div className="relative flex gap-3">
                <div className="mt-1 rounded-xl bg-white/20 p-2.5">
                  <MessageSquare size={18} />
                </div>

                <div>
                  <p className="text-2xl font-extrabold">Contact Admin</p>
                  <p className="mt-1 max-w-[260px] text-sm text-emerald-50">
                    Chat directly with our admin team for personalized assistance
                  </p>

                  <a
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-white"
                    href={`https://wa.me/${ADMIN_PHONE}?text=Hello%20I%20need%20help%20with%20my%20order`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Start Chat
                    <ExternalLink size={14} />
                  </a>
                </div>
              </div>
            </div>

            <div className={`rounded-3xl border p-4 ${isDark ? "border-slate-700 bg-slate-900" : "border-[#d7d8de] bg-[#f6f6f8]"}`}>
              <div className="flex items-start gap-3">
                <div className={`rounded-xl p-2.5 ${isDark ? "bg-slate-800 text-slate-300" : "bg-[#e8e9ed] text-slate-500"}`}>
                  <Clock3 size={18} />
                </div>

                <div className={isDark ? "text-slate-300" : "text-slate-600"}>
                  <p className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-slate-900"}`}>Support Hours</p>
                  <p className="mt-1 text-sm leading-relaxed">
                    Our support team is available <span className={`font-bold ${isDark ? "text-white" : "text-slate-900"}`}>Monday to Sunday, 7:00 AM - 10:00 PM (GMT)</span>. We aim to respond to all messages within 24 hours.
                  </p>
                </div>
              </div>
            </div>

            <div className={`rounded-3xl border p-4 ${isDark ? "border-slate-700 bg-slate-900" : "border-[#d7d8de] bg-[#f6f6f8]"}`}>
              <div className="flex items-start gap-3">
                <div className={`rounded-xl p-2.5 ${isDark ? "bg-slate-800 text-slate-300" : "bg-[#e8e9ed] text-slate-500"}`}>
                  <Code2 size={18} />
                </div>

                <div className={isDark ? "text-slate-300" : "text-slate-600"}>
                  <p className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-slate-900"}`}>About Us</p>
                  <p className="mt-1 text-sm leading-relaxed">
                    Data fie helps you buy data quickly with trusted support and fast delivery updates.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "api" && (
          <div className="mx-auto w-full max-w-md">
            <ApiDocument profile={profile} />
          </div>
        )}

        {activeTab === "profile" && (
          <div className="mx-auto w-full max-w-md space-y-5">
            <div className="overflow-hidden rounded-3xl bg-[linear-gradient(135deg,#1e3a8a_0%,#2563eb_50%,#60a5fa_100%)] px-5 py-5 text-white shadow-[0_16px_34px_rgba(37,99,235,0.34)]">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 text-2xl font-black uppercase text-white backdrop-blur-sm">
                    {firstName.slice(0, 1)}
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-100">Agent Profile</p>
                    <p className="mt-1 text-2xl font-black leading-tight">{profileName}</p>
                    <p className="mt-1 text-sm text-blue-100">{profileEmail}</p>
                  </div>
                </div>
                <div className="rounded-full bg-white/15 px-3 py-1 text-xs font-bold text-white">Active</div>
              </div>

              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-100">API Docs</p>
                  <p className="text-sm text-blue-100">Only visible after click.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveTab("api")}
                  className="inline-flex items-center justify-center rounded-2xl bg-white px-4 py-2 text-sm font-semibold text-slate-900 shadow-sm transition hover:bg-slate-100"
                >
                  View API Documentation
                </button>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-2xl bg-white/12 p-3 backdrop-blur-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-100">Phone</p>
                  <p className="mt-1 font-bold text-white">{profilePhone}</p>
                </div>
                <div className="rounded-2xl bg-white/12 p-3 backdrop-blur-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-100">Website</p>
                  {profile?.websiteUrl ? (
                    <a
                      href={profile.websiteUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-1 block font-bold text-white underline"
                    >
                      {profile.websiteUrl}
                    </a>
                  ) : (
                    <p className="mt-1 font-bold text-white/80">Not set</p>
                  )}
                </div>
                <div className="rounded-2xl bg-white/12 p-3 backdrop-blur-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-100">API Key</p>
                  <p className="mt-1 font-bold text-white">{profile?.apiKey ? maskKey(profile.apiKey) : "Not set"}</p>
                </div>
                <div className="rounded-2xl bg-white/12 p-3 backdrop-blur-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-100">Wallet</p>
                  <p className="mt-1 font-bold text-white">{formatGhs(profile?.walletBalance || 0)}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className={`rounded-3xl border p-3 ${isDark ? "border-white/10 bg-slate-900" : "border-slate-200 bg-white"}`}>
                <p className={`text-[11px] font-semibold uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>Purchases</p>
                <p className={`mt-2 text-xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>{totalPurchases}</p>
              </div>
              <div className={`rounded-3xl border p-3 ${isDark ? "border-white/10 bg-slate-900" : "border-slate-200 bg-white"}`}>
                <p className={`text-[11px] font-semibold uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>Topups</p>
                <p className={`mt-2 text-xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>{totalTopups}</p>
              </div>
              <div className={`rounded-3xl border p-3 ${isDark ? "border-white/10 bg-slate-900" : "border-slate-200 bg-white"}`}>
                <p className={`text-[11px] font-semibold uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>Theme</p>
                <p className={`mt-2 text-xl font-black capitalize ${isDark ? "text-white" : "text-slate-900"}`}>{theme}</p>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <button
                onClick={() => setTheme(isDark ? "light" : "dark")}
                className={`rounded-3xl border p-4 text-left transition ${isDark ? "border-white/10 bg-slate-900 hover:bg-white/5" : "border-slate-200 bg-white hover:bg-slate-50"}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className={`rounded-2xl p-2.5 ${isDark ? "bg-slate-800 text-slate-200" : "bg-slate-100 text-slate-600"}`}>
                    <Moon size={18} />
                  </div>
                  <ChevronRight size={18} className={isDark ? "text-slate-500" : "text-slate-400"} />
                </div>
                <p className={`mt-3 text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>Dark Mode</p>
                <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  {isDark ? "Switch back to light appearance." : "Enable dark appearance for low-light use."}
                </p>
              </button>

              <button
                onClick={handleSimSetup}
                className={`rounded-3xl border p-4 text-left transition ${isDark ? "border-white/10 bg-slate-900 hover:bg-white/5" : "border-slate-200 bg-white hover:bg-slate-50"}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className={`rounded-2xl p-2.5 ${isDark ? "bg-slate-800 text-slate-200" : "bg-slate-100 text-slate-600"}`}>
                    <Smartphone size={18} />
                  </div>
                  <ChevronRight size={18} className={isDark ? "text-slate-500" : "text-slate-400"} />
                </div>
                <p className={`mt-3 text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>SIM Setup</p>
                <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>Default SIM: {savedSimNumber}</p>
              </button>
            </div>

            <div className={`rounded-3xl border p-4 ${isDark ? "border-white/10 bg-slate-900" : "border-slate-200 bg-white"}`}>
              <p className={`text-base font-extrabold ${isDark ? "text-white" : "text-slate-900"}`}>Edit Details</p>
              <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                Update your name and phone number.
              </p>

              <div className="mt-3 space-y-3">
                <div>
                  <label className={`mb-1.5 block text-xs font-bold uppercase tracking-wide ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                    Full Name
                  </label>
                  <input
                    value={profileFormName}
                    onChange={(event) => setProfileFormName(event.target.value)}
                    placeholder="Enter full name"
                    className={`w-full rounded-2xl border px-4 py-3 text-sm font-semibold outline-none transition-all ${isDark ? "border-slate-600 bg-slate-800 text-white focus:border-[#274193] focus:ring-2 focus:ring-[#274193]/20" : "border-slate-300 bg-white text-slate-900 focus:border-[#274193] focus:ring-2 focus:ring-[#274193]/20"}`}
                  />
                </div>

                <div>
                  <label className={`mb-1.5 block text-xs font-bold uppercase tracking-wide ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                    Phone Number
                  </label>
                  <input
                    value={profileFormPhone}
                    onChange={(event) => setProfileFormPhone(event.target.value)}
                    placeholder="024xxxxxxx"
                    maxLength={10}
                    className={`w-full rounded-2xl border px-4 py-3 text-sm font-semibold outline-none transition-all ${isDark ? "border-slate-600 bg-slate-800 text-white focus:border-[#274193] focus:ring-2 focus:ring-[#274193]/20" : "border-slate-300 bg-white text-slate-900 focus:border-[#274193] focus:ring-2 focus:ring-[#274193]/20"}`}
                  />
                </div>

                <div>
                  <label className={`mb-1.5 block text-xs font-bold uppercase tracking-wide ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                    Website URL
                  </label>
                  <input
                    value={profileFormWebsite}
                    onChange={(event) => setProfileFormWebsite(event.target.value)}
                    placeholder="https://yourstore.example.com"
                    className={`w-full rounded-2xl border px-4 py-3 text-sm font-semibold outline-none transition-all ${isDark ? "border-slate-600 bg-slate-800 text-white focus:border-[#274193] focus:ring-2 focus:ring-[#274193]/20" : "border-slate-300 bg-white text-slate-900 focus:border-[#274193] focus:ring-2 focus:ring-[#274193]/20"}`}
                  />
                </div>

                <button
                  onClick={handleProfileUpdate}
                  disabled={savingProfile || !profileFormChanged}
                  className={`w-full rounded-2xl px-4 py-3 text-sm font-bold transition-all ${
                    !savingProfile && profileFormChanged
                      ? "bg-[#274193] text-white hover:bg-[#1e3070]"
                      : "bg-[#e5e5ea] text-slate-500"
                  }`}
                >
                  {savingProfile ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>

            <div className="rounded-3xl bg-[linear-gradient(135deg,#3b82f6_0%,#1d4ed8_65%,#1e40af_100%)] px-5 py-5 text-white shadow-[0_14px_30px_rgba(30,64,175,0.35)]">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xl font-black">Invite Friends</p>
                  <p className="mt-1 text-sm font-semibold text-blue-100">Share the joy and grow your circle.</p>
                </div>
                <div className="rounded-2xl bg-white/15 p-3">
                  <User size={20} />
                </div>
              </div>
            </div>

            <button
              onClick={handleLogout}
              className={`flex w-full items-center justify-between rounded-3xl border px-4 py-4 text-left transition ${isDark ? "border-red-500/30 bg-red-500/10 hover:bg-red-500/15" : "border-red-200 bg-red-50 hover:bg-red-100"}`}
            >
              <span className="inline-flex items-center gap-3 text-base font-bold text-red-500">
                <LogOut size={20} />
                Logout
              </span>
              <ChevronRight size={18} className="text-red-300" />
            </button>

            <p className={`text-center text-sm font-semibold ${isDark ? "text-slate-400" : "text-slate-400"}`}>v.1.0.43</p>
          </div>
        )}

          <AnimatePresence>
            {showPackagePicker && activeTab === "home" && serviceTab === "data" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[70] bg-black/25"
              >
                <motion.div
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  exit={{ y: "100%" }}
                  transition={{ type: "spring", damping: 30, stiffness: 260 }}
                  className="absolute bottom-0 left-0 right-0 mx-auto w-full max-w-md rounded-t-3xl border border-[#d8d8de] bg-[#f5f5f7]"
                >
                  <div className="flex items-center justify-between border-b border-[#dee0e6] px-4 py-4">
                    <h3 className="text-sm font-semibold text-slate-900">Select Package</h3>
                    <button
                      type="button"
                      onClick={() => setShowPackagePicker(false)}
                      className="rounded-full p-1 text-slate-500"
                      aria-label="Close package picker"
                    >
                      <X size={24} />
                    </button>
                  </div>

                  <div className="max-h-[58vh] overflow-y-auto pb-2">
                    {filteredPackages.map((pkg) => {
                      const selected = selectedPackageId === pkg.id;

                      return (
                        <button
                          key={pkg.id}
                          type="button"
                          onClick={() => {
                            setSelectedPackageId(pkg.id);
                            setShowPackagePicker(false);
                          }}
                          className={`flex w-full items-center justify-between border-b border-[#e3e5ea] px-4 py-2.5 text-left transition ${
                            selected ? "bg-[#e8eef8]" : "bg-[#f5f5f7]"
                          }`}
                        >
                          <div className="flex items-center gap-2.5">
                            <span
                              className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                                selected ? "border-[#2d5fd2] bg-[#2d5fd2] text-white" : "border-[#afb4be]"
                              }`}
                            >
                              {selected && <CheckCircle size={10} />}
                            </span>
                            <span className="text-sm font-semibold text-slate-800">{pkg.id}</span>
                          </div>

                          <span className="text-sm font-semibold text-slate-800">{formatGhs(pkg.promoPrice)}</span>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
      </div>

        <BottomNav activeTab={activeTab} setActiveTab={setActiveTab} />
    </div>
  );
}
